/**
 * custom.js — Source-agnostic zip code handler
 *
 * Works identically with ASL, ipapi.co, and the Mulch Mound geo
 * service ('mm'). asl_snippet.liquid detects the zip (the "how"),
 * then calls window.asl_loaded() which is defined here and owns all
 * post-detection behavior (the "what happens after").
 *
 * Responsibilities:
 *   - Initialize zip state + analytics values
 *   - Wire all modal event handlers (Apply, Change, Done, zone-select)
 *   - Live-validate typed zips against /api/public/zip-info (mm mode)
 *   - Clear cart on zip change ONLY when the new zip is served by
 *     different products (mm mode; asl/ipapi always clear)
 *   - Redirect to /collections/all when zip changes on a PDP
 *   - Intercept add-to-cart to stamp cartZipValue for analytics
 */

// Verbose zip logging — off in production. Flip to true to debug the flow.
var MM_DEBUG = false;

window.close_modal = function () {
  $("#asl_popup_wrapper").hide();
};

window.show_modal = function () {
  $("#asl_popup_wrapper").show();
};

function buildOverrideUrl(paramName, paramValue) {
  const href = window.location.href;
  const joinChar = href.indexOf("?") === -1 ? "?" : "&";
  return href + joinChar + paramName + "=" + paramValue;
}

// Helper to retrieve the current zip from localStorage.
function getCurrentZip() {
  const storedOverride = localStorage.getItem("zip_override");
  if (storedOverride && storedOverride.length >= 5 && storedOverride !== "null") {
    return storedOverride;
  }
  const detected = localStorage.getItem("detectedZip");
  if (detected && detected !== "null") {
    return detected;
  }
  return null;
}

// Helper to update the zip in localStorage.
function setCurrentZip(zip) {
  if (zip && zip.length >= 5) {
    localStorage.setItem("zip_override", zip);
  }
}

/* ================================================================
   Shared zip change handler — called by ALL zip change entry points
   Clears cart, updates all localStorage keys, navigates.
   ================================================================ */
let zipChangeInProgress = false;

function applyZipChange(newZip) {
  if (zipChangeInProgress) return;
  zipChangeInProgress = true;

  // Update all localStorage keys
  localStorage.setItem("zip_override", newZip);
  localStorage.setItem("currentZipValue", newZip);
  localStorage.setItem("cartZipValue", newZip);
  localStorage.removeItem("zipVariantDataCache");

  // Update global
  window.currentZip = newZip;

  MM_DEBUG && console.log("applyZipChange() => evaluating cart for zip:", newZip);

  function navigate() {
    if (window.location.pathname.startsWith("/products")) {
      window.location.href = "/collections/all";
    } else {
      window.location.reload();
    }
  }
  function clearCartAndGo() {
    fetch("/cart/clear.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).finally(navigate);
  }

  // mm mode: only clear the cart when the new zip is served by different
  // products (different winning supplier/prices). If every cart item is
  // still sold under the new zip, the cart survives the move.
  if (typeof window.mmZipInfo !== "function") {
    clearCartAndGo();
    return;
  }
  Promise.all([
    fetch("/cart.js").then(function (r) { return r.json(); }),
    window.mmZipInfo(newZip),
  ])
    .then(function (results) {
      var cart = results[0];
      var info = results[1];
      try {
        localStorage.setItem("zip_meta", JSON.stringify({
          zip: newZip, city: info.city, state: info.state,
          in_service: info.in_service, ts: Date.now(),
        }));
      } catch (e) {}
      var itemIds = (cart.items || []).map(function (i) { return String(i.variant_id); });
      if (!itemIds.length) { navigate(); return; }
      var available = (info.variants || []).map(String);
      var allAvailable = itemIds.every(function (id) { return available.indexOf(id) !== -1; });
      if (allAvailable) {
        MM_DEBUG && console.log("applyZipChange() => same products serve", newZip, "— keeping cart");
        navigate();
      } else {
        clearCartAndGo();
      }
    })
    .catch(clearCartAndGo);
}

/* ================================================================
   Intercept add-to-cart to stamp cartZipValue (from zip-cart-guard)
   ================================================================ */
function interceptAddToCart() {
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const [url] = args;
    if (url && (url.includes("/cart/add") || url === "/cart/add.js")) {
      const zip = getCurrentZip();
      if (zip && !localStorage.getItem("cartZipValue")) {
        localStorage.setItem("cartZipValue", zip);
        MM_DEBUG && console.log("interceptAddToCart() => stamped cartZipValue:", zip);
      }
    }
    return originalFetch.apply(this, args);
  };
}

/* ================================================================
   asl_loaded() — Called by BOTH ASL remote script and ipapi.co
   block after zip detection is complete. Owns all post-detection
   behavior: state init, analytics, event handlers.
   ================================================================ */
window.asl_loaded = function () {
  let aslInputZip = false;
  const $zipInput = $("#enter_zip_code");
  const $popupWrapper = $("#asl_popup_wrapper");
  const $doneBtn = $(".done_btn");
  $popupWrapper.hide();

  // --- Initialization: Persist initial detected zip once ---
  let storedDetectedZip = localStorage.getItem("detectedZip");
  if (!storedDetectedZip || storedDetectedZip === "null") {
    try {
      const zipFromFunction = asl_zip(); // Only exists in ASL mode
      storedDetectedZip = zipFromFunction;
      localStorage.setItem("detectedZip", zipFromFunction);
    } catch (err) {
      // Expected in ipapi mode — asl_zip() doesn't exist
      MM_DEBUG && console.log("asl_loaded() => asl_zip() not available (ipapi mode)");
    }
  }

  // --- Set the current zip from localStorage ---
  var currentZip = getCurrentZip();
  // Fallback to the detected zip if needed.
  if (!currentZip || currentZip.length < 5) {
    currentZip = storedDetectedZip;
  }
  // Also assign it to the global variable.
  window.currentZip = currentZip;
  // Store in localStorage for analytics or other usage.
  if (currentZip) {
    localStorage.setItem("currentZipValue", currentZip);
  }

  // Input stays empty — placeholder text tells the customer to type a
  // 5-digit zip; the current location shows as display text above it.
  MM_DEBUG && console.log("asl_loaded() => detectedZip:", storedDetectedZip);
  MM_DEBUG && console.log("asl_loaded() => currentZip:", currentZip);

  // Mark the default address based on currentZip.
  $('[data-zip="' + currentZip + '"]')
    .addClass("asl-default-address")
    .append("<span>Default address</span>");

  // Only show the modal if there's no valid zip_override and the popup wasn't previously rejected.
  const storedZip = localStorage.getItem("zip_override");
  const rejectPopup = localStorage.getItem("reject_asl_popup");
  if ((!storedZip || storedZip.length < 5 || storedZip === "null") && !rejectPopup) {
    window.show_modal();
  }

  // --- Intercept add-to-cart for cartZipValue stamping ---
  interceptAddToCart();

  // --- Event Handlers ---

  function handleDoneButton() {
    const zipcode = $zipInput.val();
    if (aslInputZip && zipcode.length > 4) {
      if (zipcode !== currentZip) {
        currentZip = zipcode;
        applyZipChange(zipcode);
      } else {
        // Same zip, just close
        window.close_modal();
      }
    } else {
      localStorage.setItem("reject_asl_popup", 1);
      window.close_modal();
    }
  }

  $doneBtn.on("click", function (e) {
    e.preventDefault();
    handleDoneButton();
  });

  $zipInput.on("keydown", function (e) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleDoneButton();
    }
  });

  $(".change-zip-code").on("click", function (e) {
    e.preventDefault();
    aslInputZip = true;
    $(".asl-zip-code-input").show();
    $(".asl-zip-code").hide();
  });

  $(".asl-popup #country").on("change", function () {
    const val = $(this).val();
    if (val.length > 1) {
      window.location.replace(buildOverrideUrl("country_override", val));
    }
  });

  $("body").on("click", ".asl-zip-code-input-apply", function (e) {
    e.preventDefault();
    const zipcode = $zipInput.val();
    try {
      if (zipcode === currentZip) {
        window.close_modal();
      } else if (zipcode.length >= 5) {
        currentZip = zipcode;
        applyZipChange(zipcode);
      }
    } catch (err) {
      console.error("Error in .asl-zip-code-input-apply:", err);
    }
  });

  // Wire the #close_popup (Apply/Done) button with capture phase
  // so it runs before theme.liquid's bubble-phase handler.
  // Note: #close_popup is the same element as .done_btn
  // (a#close_popup.asl-btn.done_btn). The capture handler
  // intercepts zip changes; the jQuery .done_btn handler above
  // handles the non-edit-mode "reject popup" case.
  var applyEl = document.getElementById("close_popup");
  if (applyEl && !applyEl.dataset.zipWired) {
    applyEl.dataset.zipWired = "true";
    applyEl.addEventListener(
      "click",
      function (e) {
        var input = document.querySelector(".asl-zip-code-input input");
        var newZip = input && input.value ? input.value.trim() : "";
        if (newZip && /^\d{5}$/.test(newZip) && newZip !== currentZip) {
          e.preventDefault();
          e.stopImmediatePropagation();
          currentZip = newZip;
          applyZipChange(newZip);
        }
        // If same zip or no valid input, fall through to
        // theme.liquid's handler which closes the modal
      },
      true
    ); // capture phase
  }

  $("body").on("click", ".header-delivery", function (e) {
    // The modal gets moved INSIDE .header-delivery on desktop
    // (theme.liquid) — clicks inside it must not re-open the modal,
    // otherwise Apply/Done closes and instantly re-opens it.
    var modal = document.getElementById("asl_popup_wrapper");
    if (modal && modal.contains(e.target)) return;
    window.show_modal();
  });

  $("body").on("click", ".zone-select", function () {
    const zipcode = $(this).attr("data-zip");
    try {
      if (zipcode === currentZip) {
        window.close_modal();
      } else if (zipcode.length >= 5) {
        currentZip = zipcode;
        applyZipChange(zipcode);
      }
    } catch (err) {
      console.error("Error in .zone-select:", err);
    }
  });

  // --- Live zip validation while typing (mm mode only) ---
  // Shows "City, ST — we deliver here!" / "not in our delivery area yet"
  // under the input before the customer commits.
  // OFF for now (Alec, 2026-06-12) — flip to true to re-enable.
  var MM_LIVE_VALIDATE = false;
  var mmFeedbackEl = null;
  function mmEnsureFeedback() {
    if (mmFeedbackEl) return mmFeedbackEl;
    var container = document.querySelector(".asl-zip-code-input");
    if (!container) return null;
    mmFeedbackEl = document.createElement("div");
    mmFeedbackEl.className = "mm-zip-feedback";
    container.appendChild(mmFeedbackEl);
    return mmFeedbackEl;
  }
  var mmValidateTimer = null;
  function mmLiveValidate() {
    if (!MM_LIVE_VALIDATE) return;
    if (typeof window.mmZipInfo !== "function") return;
    var el = mmEnsureFeedback();
    if (!el) return;
    var zip = ($zipInput.val() || "").trim();
    if (!/^\d{5}$/.test(zip)) {
      el.textContent = "";
      el.className = "mm-zip-feedback";
      return;
    }
    clearTimeout(mmValidateTimer);
    mmValidateTimer = setTimeout(function () {
      window.mmZipInfo(zip)
        .then(function (info) {
          if (($zipInput.val() || "").trim() !== zip) return;
          if (info.in_service) {
            el.textContent = "✓ " + info.city + ", " + info.state + " — we deliver here!";
            el.className = "mm-zip-feedback mm-zip-good";
          } else if (info.known) {
            el.textContent = info.city + ", " + info.state + " — not in our delivery area yet";
            el.className = "mm-zip-feedback mm-zip-bad";
          } else {
            el.textContent = "That zip doesn't look right";
            el.className = "mm-zip-feedback mm-zip-bad";
          }
          try {
            localStorage.setItem("zip_meta", JSON.stringify({
              zip: zip, city: info.city, state: info.state,
              in_service: info.in_service, ts: Date.now(),
            }));
          } catch (e) {}
        })
        .catch(function () { el.textContent = ""; });
    }, 250);
  }
  $zipInput.on("input", mmLiveValidate);

  MM_DEBUG && console.log("asl_loaded() => all handlers wired");
};